import { useState, useEffect } from 'react'
import { X, Link2, Trash2, Search, ChevronRight, ChevronLeft } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import {
  STMIDBEquipmentService,
  getWLLCategoryTitle,
  mapSTMIDBToWLLCategoryCode,
} from '@/services/api/stmidbEquipmentService'
import { isSlingCategory, matchesAnyCategoryFamily } from '@/utils/categoryFamily'

// Touch-friendly dimensions matching Samleliste
const TOUCH = {
  buttonHeight: 'min-h-[48px]',
  inputHeight: 'min-h-[48px]',
  fontSize: 'text-base',
}

interface SubEquipment {
  relationship_id: string
  child_equipment_id: string
  child_name: string
  child_serial_number: string | null
  child_category_code: string
  child_category_title: string
  wll?: string | null
  quantity?: number | null
  manufacturer?: string | null
}

interface AvailableEquipment {
  id: string
  name: string
  serial_number: string | null
  asset_number: string
  category_code: string
  category_title: string
}

interface SubEquipmentModalProps {
  isOpen: boolean
  onClose: () => void
  mainEquipmentId: string
  mainEquipmentName: string
  companyId: string
  onSuccess?: () => void
  initialMode?: 'view' | 'connect'
  excludeSubEquipmentId?: string | null
  replaceCategoryCode?: string | null
  /** 'stmidb' for SLB: relationships are STMIDB's parent_equipment_id. */
  source?: 'stmidb'
}

const isOffshoreShackle = (code: string) => {
  const c = (code || '').toUpperCase()
  return c === 'R4-2' || c.startsWith('R4-2-')
}

const isSling = (code: string) => isSlingCategory(code)

const isAllowedAttachCategory = (code: string) =>
  matchesAnyCategoryFamily(code, ['R1', 'R2', 'R3', 'R4'])

const matchesReplaceCategory = (candidate: string, replaceWith: string) => {
  if (isOffshoreShackle(replaceWith)) return isOffshoreShackle(candidate)
  if (isSling(replaceWith)) return isSling(candidate)
  return isAllowedAttachCategory(candidate)
}

const SubEquipmentModal = ({
  isOpen,
  onClose,
  mainEquipmentId,
  mainEquipmentName,
  companyId,
  onSuccess,
  initialMode = 'view',
  excludeSubEquipmentId,
  replaceCategoryCode,
  source,
}: SubEquipmentModalProps) => {
  const isSLB = source === 'stmidb'
  const [subEquipment, setSubEquipment] = useState<SubEquipment[]>([])
  const [availableEquipment, setAvailableEquipment] = useState<AvailableEquipment[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<Set<string>>(new Set())
  const [mode, setMode] = useState<'view' | 'connect'>(initialMode)

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode)
      setSelectedEquipmentIds(new Set())
      setSearchTerm('')
      if (initialMode === 'connect') {
        loadAvailableEquipment()
      } else {
        loadSubEquipment()
      }
    }
  }, [isOpen])

  const loadSubEquipment = async () => {
    try {
      setLoading(true)
      if (isSLB) {
        const children = await STMIDBEquipmentService.getSubEquipment(mainEquipmentId)
        setSubEquipment(children.map(child => {
          const code = mapSTMIDBToWLLCategoryCode(child.category, child.equipment_type)
          return {
            relationship_id: child.id,
            child_equipment_id: child.id,
            child_name: child.description || child.equipment_type || 'Unknown',
            child_serial_number: child.serial_number,
            child_category_code: code || '',
            child_category_title: getWLLCategoryTitle(child.category, code, child.equipment_type),
          }
        }))
        return
      }

      const { data, error } = await supabase
        .from('equipment_relationship_v2')
        .select(`
          id,
          sub_equipment_id,
          equipment_v2!sub_equipment_id (
            id,
            name,
            serial_number,
            categories_v2 (code, title_en)
          )
        `)
        .eq('main_equipment_id', mainEquipmentId)

      if (error) throw error

      // Fetch WLL and quantity for each sub-equipment
      const subEqWithSpecs = await Promise.all(
        (data || []).map(async (item) => {
          const equipmentId = item.sub_equipment_id
          
          // Fetch WLL, quantity, and manufacturer specifications
          const { data: specs } = await supabase
            .from('equipment_specifications_v2')
            .select(`
              spec_key,
              value_numeric,
              value_integer,
              value_text,
              value_reference_id,
              unit:units_v2!equipment_specifications_v2_unit_id_fkey(symbol)
            `)
            .eq('equipment_id', equipmentId)
            .in('spec_key', ['wll', 'swl', 'quantity', 'qty', 'manufacturer'])

          const wllSpec = specs?.find(s => ['wll', 'swl'].includes(s.spec_key))
          const quantitySpec = specs?.find(s => ['quantity', 'qty'].includes(s.spec_key))
          const manufacturerSpec = specs?.find(s => s.spec_key === 'manufacturer')
          
          let wllDisplay = null
          if (wllSpec) {
            const value = wllSpec.value_numeric || wllSpec.value_integer || wllSpec.value_text
            const unit = (wllSpec.unit as any)?.symbol || ''
            wllDisplay = value ? `${value} ${unit}`.trim() : null
          }

          const quantity = quantitySpec?.value_integer || quantitySpec?.value_numeric || null

          // Fetch manufacturer name if reference ID exists
          let manufacturerName = null
          if (manufacturerSpec?.value_reference_id) {
            const { data: mfr } = await supabase
              .from('manufacturers_v2')
              .select('name')
              .eq('id', manufacturerSpec.value_reference_id)
              .single()
            manufacturerName = mfr?.name || null
          } else if (manufacturerSpec?.value_text) {
            // Try fetching by name or ID stored in value_text
            const textValue = manufacturerSpec.value_text
            // Check if it's a UUID (legacy data)
            if (textValue.length === 36 && textValue.includes('-')) {
              const { data: mfr } = await supabase
                .from('manufacturers_v2')
                .select('name')
                .eq('id', textValue)
                .single()
              manufacturerName = mfr?.name || null
            } else {
              // It's already a manufacturer name
              manufacturerName = textValue
            }
          }

          return {
            relationship_id: item.id,
            child_equipment_id: item.sub_equipment_id,
            child_name: (item.equipment_v2 as any)?.name || 'Unknown',
            child_serial_number: (item.equipment_v2 as any)?.serial_number,
            child_category_code: (item.equipment_v2 as any)?.categories_v2?.code || '',
            child_category_title: (item.equipment_v2 as any)?.categories_v2?.title_en || '',
            wll: wllDisplay,
            quantity: quantity,
            manufacturer: manufacturerName
          }
        })
      )

      setSubEquipment(subEqWithSpecs)
    } catch (error) {
      console.error('Error loading sub-equipment:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadAvailableEquipment = async () => {
    try {
      setLoading(true)

      if (isSLB) {
        // Server side returns only unattached R1/R3/R4/WL rows and excludes
        // the parent's ancestors, so no cycles can be created.
        const rows = await STMIDBEquipmentService.searchAttachableEquipment(mainEquipmentId, undefined, 1000)
        setAvailableEquipment(rows
          .map(row => {
            const code = mapSTMIDBToWLLCategoryCode(row.category, row.equipment_type) || ''
            return {
              id: row.id,
              name: row.description || row.equipment_type || '',
              serial_number: row.serial_number,
              asset_number: row.equipment_code || row.asset_number || '',
              category_code: code,
              category_title: getWLLCategoryTitle(row.category, code, row.equipment_type),
            }
          })
          .filter(eq => !replaceCategoryCode || matchesReplaceCategory(eq.category_code, replaceCategoryCode)))
        return
      }

      // First, get all equipment that is already attached as sub-equipment to ANY main equipment
      const { data: attachedSubEquipment, error: attachedError } = await supabase
        .from('equipment_relationship_v2')
        .select('sub_equipment_id')

      if (attachedError) throw attachedError

      // Create set of IDs that are already sub-equipment somewhere
      // Exclude the item being replaced so it shows up as available
      const alreadyAttachedIds = new Set(
        (attachedSubEquipment || [])
          .map(item => item.sub_equipment_id)
          .filter(id => id !== excludeSubEquipmentId)
      )

      // Get all equipment from same company
      const { data, error } = await supabase
        .from('equipment_v2')
        .select(`
          id,
          name,
          serial_number,
          asset_number,
          company_id,
          categories_v2 (code, title_en)
        `)
        .eq('company_id', companyId)
        .neq('id', mainEquipmentId)
        .order('serial_number')

      if (error) throw error

      // Filter to R1-R4 categories and exclude already attached to ANY main equipment
      const filtered: AvailableEquipment[] = (data || [])
        .filter(eq => {
          // Check if already attached as sub-equipment anywhere
          if (alreadyAttachedIds.has(eq.id)) return false

          // Check if allowed category
          const categoryCode = (eq.categories_v2 as any)?.code || ''
          if (replaceCategoryCode) {
            return matchesReplaceCategory(categoryCode, replaceCategoryCode)
          }
          return isAllowedAttachCategory(categoryCode)
        })
        .map(eq => ({
          id: eq.id,
          name: eq.name,
          serial_number: eq.serial_number,
          asset_number: eq.asset_number,
          category_code: (eq.categories_v2 as any)?.code || '',
          category_title: (eq.categories_v2 as any)?.title_en || ''
        }))

      setAvailableEquipment(filtered)
    } catch (error) {
      console.error('Error loading available equipment:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDisconnect = async (relationshipId: string, equipmentName: string) => {
    if (!confirm(`Koble fra "${equipmentName}"?\n\nDette fjerner bare koblingen, ikke utstyret selv.`)) {
      return
    }

    try {
      if (isSLB) {
        await STMIDBEquipmentService.setParentEquipment(relationshipId, null)
      } else {
        const { error } = await supabase
          .from('equipment_relationship_v2')
          .delete()
          .eq('id', relationshipId)

        if (error) throw error
      }

      await loadSubEquipment()
      if (onSuccess) onSuccess()
    } catch (error) {
      console.error('Error disconnecting sub-equipment:', error)
      alert('Kunne ikke koble fra underutstyr')
    }
  }

  const handleConnect = async () => {
    if (selectedEquipmentIds.size === 0) return

    try {
      setConnecting(true)
      if (isSLB) {
        for (const childId of selectedEquipmentIds) {
          await STMIDBEquipmentService.setParentEquipment(childId, mainEquipmentId)
        }
      } else {
        const relationships = Array.from(selectedEquipmentIds).map(subEquipmentId => ({
          main_equipment_id: mainEquipmentId,
          sub_equipment_id: subEquipmentId
        }))

        const { error } = await supabase
          .from('equipment_relationship_v2')
          .insert(relationships)

        if (error) throw error
      }

      setMode('view')
      setSelectedEquipmentIds(new Set())
      setSearchTerm('')
      await loadSubEquipment()
      if (onSuccess) onSuccess()
    } catch (error) {
      console.error('Error connecting sub-equipment:', error)
      alert('Kunne ikke koble til underutstyr')
    } finally {
      setConnecting(false)
    }
  }

  const toggleSelection = (equipmentId: string) => {
    setSelectedEquipmentIds(prev => {
      if (replaceCategoryCode) {
        return prev.has(equipmentId) ? new Set() : new Set([equipmentId])
      }
      const next = new Set(prev)
      if (next.has(equipmentId)) next.delete(equipmentId)
      else next.add(equipmentId)
      return next
    })
  }

  const filteredAvailable = availableEquipment.filter(eq => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      eq.name.toLowerCase().includes(search) ||
      eq.serial_number?.toLowerCase().includes(search) ||
      eq.asset_number.toLowerCase().includes(search)
    )
  })

  // Extract parent code from category code
  const getParentCode = (code: string) => {
    return code.match(/^([A-Z]+\d+)/)?.[1] || code
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
        {/* Header - Samleliste style */}
        <div className="flex items-center justify-between p-5 border-b-2 border-stone-200 bg-stone-50">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-[#1E3A5F]">
              {replaceCategoryCode ? 'Bytt underutstyr' : 'Underutstyr'}
            </h2>
            <p className="text-sm text-stone-500 mt-0.5 truncate">{mainEquipmentName}</p>
          </div>
          <button
            onClick={onClose}
            className={`${TOUCH.buttonHeight} w-12 flex items-center justify-center text-stone-400 hover:text-stone-600 hover:bg-stone-200 rounded-xl transition-colors flex-shrink-0`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {mode === 'view' ? (
            // View Mode - List Connected Sub-Equipment
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-semibold text-stone-500">
                  Tilkoblet ({subEquipment.length})
                </h3>
                <button
                  onClick={() => {
                    setMode('connect')
                    loadAvailableEquipment()
                  }}
                  className={`flex items-center gap-2 px-4 py-2 ${TOUCH.fontSize} font-medium text-white bg-[#1E3A5F] rounded-xl hover:bg-[#2a4a73] active:scale-[0.98] transition-all`}
                >
                  <Link2 size={18} />
                  <span>Koble til</span>
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12 gap-3">
                  <div className="w-6 h-6 border-2 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
                  <span className="text-base text-stone-500">Laster...</span>
                </div>
              ) : subEquipment.length === 0 ? (
                <div className="bg-stone-50 border-2 border-dashed border-stone-300 rounded-xl p-8 text-center">
                  <Link2 size={32} className="mx-auto text-stone-400 mb-2" />
                  <p className="text-sm text-stone-500">Ingen underutstyr koblet til</p>
                  <p className="text-xs text-stone-400 mt-1">Klikk "Koble til" for å legge til</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {subEquipment.map((item) => (
                    <div
                      key={item.relationship_id}
                      className="flex items-center gap-3 bg-stone-50 border-2 border-stone-200 rounded-xl p-3 hover:border-stone-300 transition-colors"
                    >
                      <Link2 size={20} className="text-[#1E3A5F] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-stone-800">
                          {item.child_serial_number || 'Ukjent SN'}
                        </p>
                        <p className="text-xs text-stone-500 truncate">
                          {item.child_name}
                          {item.manufacturer && ` (${item.manufacturer})`}
                        </p>
                        {(item.quantity || item.wll) && (
                          <div className="flex gap-2 mt-1">
                            {item.quantity && item.quantity > 1 && (
                              <span className="text-xs text-stone-600 font-medium bg-stone-200 px-1.5 py-0.5 rounded">
                                Ant: {item.quantity}
                              </span>
                            )}
                            {item.wll && (
                              <span className="text-xs text-stone-600 font-medium bg-stone-200 px-1.5 py-0.5 rounded">
                                WLL: {item.wll}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleDisconnect(item.relationship_id, item.child_name)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        title="Koble fra"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            // Connect Mode - Select Equipment to Connect
            <>
              <div className="mb-4">
                <button
                  onClick={() => setMode('view')}
                  className="flex items-center gap-1 text-sm text-[#1E3A5F] hover:text-[#2a4a73] mb-3 font-medium"
                >
                  <ChevronLeft size={18} />
                  <span>Tilbake</span>
                </button>
                <h3 className="text-sm font-semibold text-stone-500 mb-1">
                  {replaceCategoryCode ? 'Velg erstatning' : 'Velg utstyr å koble til'}
                </h3>
                <p className="text-xs text-stone-400 mb-3">
                  {replaceCategoryCode && isOffshoreShackle(replaceCategoryCode)
                    ? 'Kun sjakler'
                    : replaceCategoryCode && isSling(replaceCategoryCode)
                      ? 'Kun kategorier R1, R3'
                      : 'Kun kategorier R1, R2, R3, R4'}
                </p>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" size={20} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Søk etter navn, serienummer..."
                    className={`w-full pl-10 pr-4 py-2.5 ${TOUCH.inputHeight} ${TOUCH.fontSize} border-2 border-stone-300 rounded-xl focus:outline-none focus:border-[#1E3A5F] text-stone-800`}
                  />
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12 gap-3">
                  <div className="w-6 h-6 border-2 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
                  <span className="text-base text-stone-500">Laster...</span>
                </div>
              ) : filteredAvailable.length === 0 ? (
                <div className="bg-stone-50 border-2 border-dashed border-stone-300 rounded-xl p-8 text-center">
                  <Search size={32} className="mx-auto text-stone-400 mb-2" />
                  <p className="text-sm text-stone-500">
                    {searchTerm ? 'Ingen utstyr funnet' : 'Ingen tilgjengelig utstyr'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {filteredAvailable.map((eq) => (
                    <label
                      key={eq.id}
                      className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                        selectedEquipmentIds.has(eq.id)
                          ? 'border-[#1E3A5F] bg-blue-50'
                          : 'border-stone-200 hover:border-stone-300 bg-stone-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedEquipmentIds.has(eq.id)}
                        onChange={() => toggleSelection(eq.id)}
                        className="w-5 h-5 rounded border-2 border-stone-300 text-[#1E3A5F] focus:ring-[#1E3A5F]"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-stone-800">
                          {eq.serial_number || eq.asset_number}
                        </p>
                        <p className="text-xs text-stone-500 truncate">{eq.name}</p>
                        <p className="text-xs text-stone-400">
                          {getParentCode(eq.category_code)}
                        </p>
                      </div>
                      <ChevronRight size={18} className="text-stone-400 flex-shrink-0" />
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer - Only show in connect mode */}
        {mode === 'connect' && (
          <div className="border-t-2 border-stone-200 p-5 flex items-center gap-3 bg-stone-50">
            <button
              onClick={() => setMode('view')}
              className={`flex-1 ${TOUCH.buttonHeight} px-4 py-2.5 ${TOUCH.fontSize} font-medium text-stone-600 bg-white border-2 border-stone-300 rounded-xl hover:bg-stone-100 active:scale-[0.98] transition-all`}
            >
              Avbryt
            </button>
            <button
              onClick={handleConnect}
              disabled={selectedEquipmentIds.size === 0 || connecting}
              className={`flex-1 flex items-center justify-center gap-2 ${TOUCH.buttonHeight} px-4 py-2.5 ${TOUCH.fontSize} font-medium text-white bg-[#1E3A5F] rounded-xl hover:bg-[#2a4a73] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {connecting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Kobler til...</span>
                </>
              ) : (
                <>
                  <Link2 size={18} />
                  <span>{replaceCategoryCode ? 'Bytt' : `Koble til (${selectedEquipmentIds.size})`}</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default SubEquipmentModal
